import type { ChatProps } from "./chat-view-props.ts";

function adjustTextareaHeight(el: HTMLTextAreaElement) {
  // Clear inline minHeight from a previous growth so scrollHeight measures the
  // natural content height. Without this, after a tall paste the inline
  // minHeight (e.g. "320px") floors scrollHeight to that value, so clearing
  // the textarea content never shrinks the element back to its normal size.
  el.style.height = "auto";
  el.style.minHeight = "";
  const contentHeight = el.scrollHeight;
  if (el.closest(".chat-compose-float-dock") || el.closest(".chat-compose-inline-dock")) {
    // JAISIU-3588 (followup) — compact composer: empty = ~2 lines
    // (minPx 60), grows smoothly up to ~11 lines (maxPx 280).
    // After that, the textarea scrolls inside its own box (the
    // shell height stays fixed so the dock doesn't bounce the
    // transcript). The cap is exact (not 55vh) so the size is
    // stable across viewport heights.
    //
    // floor-only minHeight: never raise it with contentHeight, or
    // min-height can beat max-height and the flex transcript
    // sibling jitters. The post-send shrink in
    // handleComposerSend re-runs this helper, which clears
    // minHeight first so a tall paste does not floor the empty
    // state on the next render.
    const minPx = 60;
    const maxPx = 280;
    const next = Math.min(Math.max(contentHeight, minPx), maxPx);
    el.style.maxHeight = `${maxPx}px`;
    el.style.height = `${next}px`;
    el.style.minHeight = `${minPx}px`;
    return;
  }
  if (el.closest(".fleet-v2-panel--send")) {
    const minPx = 80;
    const maxPx = 280;
    const next = Math.min(Math.max(contentHeight, minPx), maxPx);
    el.style.maxHeight = "280px";
    el.style.height = `${next}px`;
    el.style.minHeight = `${minPx}px`;
    return;
  }
  // Default compose textarea
  el.style.maxHeight = "150px";
  el.style.height = `${Math.min(contentHeight, 150)}px`;
}

export function appendMentionOrSlashTrigger(draft: string, trigger: "@" | "/"): string {
  if (!draft) {
    return trigger;
  }
  // Don't double-insert if the user already typed the trigger
  // at the end of the draft.
  if (draft.endsWith(trigger)) {
    return draft;
  }
  // If the last char is a space, just append the trigger.
  if (/\s$/.test(draft)) {
    return draft + trigger;
  }
  // Otherwise, insert a separator first so the picker can
  // recognise the token boundary.
  return draft + " " + trigger;
}

/**
 * JAISIU-3588 (followup) — strip the trailing /trigger-token or
 * @trigger-token from the draft so the picker closes cleanly.
 * Used by the picker's X (close) button. Leaves any preceding
 * text intact.
 */
export function stripTrailingSlashOrMentionToken(draft: string, trigger: "@" | "/"): string {
  // Match: optional whitespace + the trigger + any token chars,
  // anchored at the END of the string.
  const re = new RegExp(`\\s*\\${trigger}[A-Za-z0-9._-]*$`);
  return draft.replace(re, "").replace(/\s+$/, "");
}

export const COMPOSE_TEXTAREA_SELECTOR =
  ".chat-compose--fleet textarea, .chat-compose-inline-dock textarea, .chat-compose-float-dock textarea";

/** Read the live composer textarea (may differ from `chatMessage` while focused). */
export function readComposeTextareaValue(): string {
  if (typeof document === "undefined") {
    return "";
  }
  const el = document.querySelector<HTMLTextAreaElement>(COMPOSE_TEXTAREA_SELECTOR);
  return el?.value ?? "";
}

/** Pull focused textarea text into `host.chatMessage` before send paths run. */
export function flushComposeDraftFromDom(host: { chatMessage: string }): void {
  if (typeof document === "undefined") {
    return;
  }
  const el = document.querySelector<HTMLTextAreaElement>(COMPOSE_TEXTAREA_SELECTOR);
  if (!el) {
    return;
  }
  host.chatMessage = el.value;
}

/** Avoid Lit `.value` binding — fleet/subagent re-renders were resetting the field while typing. */
export function syncComposeTextarea(el: HTMLTextAreaElement, sessionKey: string, draft: string) {
  const prevSession = el.dataset.chatSessionKey ?? "";
  if (prevSession !== sessionKey) {
    el.dataset.chatSessionKey = sessionKey;
    el.value = draft;
    adjustTextareaHeight(el);
    return;
  }
  if (document.activeElement === el) {
    if (!draft && el.value) {
      el.value = "";
      adjustTextareaHeight(el);
    }
    // Skip remasure while focused — @input already sizes. Re-running height:auto
    // on every parent re-render (streams, tools) collapses the flex transcript.
    return;
  }
  if (el.value !== draft) {
    el.value = draft;
    adjustTextareaHeight(el);
  }
}

/** Programmatic draft updates (Use @all, mention picker, guide try). */
export function syncChatComposeDraft(draft: string) {
  if (typeof document === "undefined") {
    return;
  }
  const el = document.querySelector<HTMLTextAreaElement>(COMPOSE_TEXTAREA_SELECTOR);
  if (!el) {
    return;
  }
  el.value = draft;
  adjustTextareaHeight(el);
}
